import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Caption1,
  Body1,
  tokens,
} from '@fluentui/react-components';
import { BrandLogo } from './BrandLogo';
import { BrandIcon } from './BrandIcon';
import {
  DataPie24Regular,
  ShieldCheckmark24Regular,
  ArrowSync24Regular,
  DocumentTable24Regular,
  Building24Regular,
  WalletCreditCard24Regular,
  Settings24Regular,
  HandshakeRegular,
  People24Regular,
  BookGlobe24Regular,
  // Key24Regular retire 2026.06 (entree Licence fusionnee dans Abonnement)
  Alert24Regular,
  Calendar24Regular,
  CheckmarkCircle24Regular,
  ChatHelp24Regular,
  LockClosed20Regular,
  Sparkle20Filled,
  Sparkle20Regular,
  ShieldCheckmark20Regular,
  Megaphone20Regular,
  BookGlobe20Regular,
  DocumentText24Regular,
} from '@fluentui/react-icons';
import { useLicense } from '../auth/LicenseGate';
import { FlagIcon } from './FlagIcon';
import { useQuery } from '@tanstack/react-query';
import { onboardingApi } from '../api/onboardingApi';
import { partnersApi } from '../api/partnersApi';

interface NavItem {
  value: string;
  /** Cle i18n du label (ex: 'nav.dashboard'). Resolu via t() au rendu. */
  labelKey: string;
  path:  string;
  icon:  JSX.Element;
  /** Gating : 'license' = active license requise. 'pro' = plan pro+. 'business' = business+. */
  requires?: 'license' | 'pro' | 'business';
  /** Cle i18n du tag a droite (ex: 'tag.pro'). Resolu via t(). 'green' = couleur verte. */
  tagKey?: string;
  /** Couleur visuelle du tag : default 'blue' (premium/pro/business), 'green' (coming soon). */
  tagColor?: 'blue' | 'green';
  /** Si true : item grise, page coming-soon (pas un upsell licence). */
  comingSoon?: boolean;
  /** v2026.06.108 - Si true, affiche un petit badge 'preview' a cote du label
   *  (gris discret, non cliquable). Pour features pas encore GA. */
  preview?: boolean;
}

const items: NavItem[] = [
  { value: 'dashboard', labelKey: 'nav.dashboard', path: '/',          icon: <DataPie24Regular /> },
  // v2026.06 :
  //   - 'controls' (Catalogue de contrôles) retire : redondant avec Compliance Manager
  //   - Compliance Manager passe AVANT CyFun Mapping (ordre fonctionnel)
  // v2026.06.94 - trial = pas d acces aux onglets de resultats / mapping / scans / manual.
  // Le dashboard reste libre pour preview, la conversion se fait via /billing.
  { value: 'compliance', labelKey: 'nav.compliance', path: '/compliance', icon: <CheckmarkCircle24Regular />, requires: 'license', tagKey: 'tag.license' },
  { value: 'cyfun',      labelKey: 'nav.cyfun',      path: '/cyfun',      icon: <BookGlobe24Regular />,       requires: 'license', tagKey: 'tag.license' },
  { value: 'm365scan',   labelKey: 'nav.m365scan',   path: '/controls',   icon: <BrandIcon name="m365" size={22} />,  requires: 'license', tagKey: 'tag.license' },
  { value: 'azure',      labelKey: 'nav.azure',      path: '/azure',      icon: <BrandIcon name="azure" size={22} />, requires: 'license', tagKey: 'tag.license' },
  { value: 'azureSchema', labelKey: 'nav.azureSchema', path: '/azure/schema', icon: <BrandIcon name="azure" size={22} />, requires: 'license', tagKey: 'tag.license' },
  { value: 'scans',      labelKey: 'nav.scans',      path: '/scans',      icon: <ArrowSync24Regular />,       requires: 'license', tagKey: 'tag.license' },
  { value: 'schedule',  labelKey: 'nav.schedule',  path: '/schedule',  icon: <Calendar24Regular />,         requires: 'pro', tagKey: 'tag.pro' },
  { value: 'reports',   labelKey: 'nav.reports',   path: '/reports',   icon: <DocumentTable24Regular />,    requires: 'license',  tagKey: 'tag.license' },
  { value: 'autodoc',   labelKey: 'nav.autodoc',   path: '/autodoc',   icon: <DocumentText24Regular />,     requires: 'license',  tagKey: 'tag.license', preview: true },
  { value: 'alerts',    labelKey: 'nav.alerts',    path: '/alerts',    icon: <Alert24Regular />,            requires: 'business', tagKey: 'tag.business' },
  { value: 'tenants',   labelKey: 'nav.tenants',   path: '/tenants',   icon: <Building24Regular />,         requires: 'license',  tagKey: 'tag.license' },
  { value: 'users',     labelKey: 'nav.users',     path: '/users',     icon: <People24Regular />,           requires: 'pro',      tagKey: 'tag.pro' },
  { value: 'billing',   labelKey: 'nav.billing',   path: '/billing',   icon: <WalletCreditCard24Regular /> },
  // Entree "Licence" supprimee 2026.06 - fusionnee dans la page Abonnement (cadre 1).
  { value: 'support',   labelKey: 'nav.support',   path: '/support',   icon: <ChatHelp24Regular /> },
  { value: 'settings',  labelKey: 'nav.settings',  path: '/settings',  icon: <Settings24Regular /> },
  { value: 'partners',  labelKey: 'nav.partners',  path: '/partners',  icon: <HandshakeRegular /> },
];

/** Plans qui ont acces aux fonctionnalites "Pro" et plus. */
const PRO_OR_HIGHER = new Set(['pro', 'business', 'enterprise']);
/** Plans qui ont acces aux fonctionnalites "Business" et plus. */
const BUSINESS_OR_HIGHER = new Set(['business', 'enterprise']);

// ---------------------------------------------------------------------------
// Actualités fetched depuis l'API marketing publique (GET /api/news)
// Click → ouvre /actualites/{slug} sur le site marketing dans un nouvel onglet.
// ---------------------------------------------------------------------------
type NewsCategory = 'product' | 'compliance' | 'security';

interface NewsItem {
  id:        string;
  slug:      string;
  date:      string;
  title:     string;
  summary:   string;
  category?: NewsCategory;
}

// URL marketing pour les liens click. Injectée au build via VITE_MARKETING_URL.
// Fallback : tente nis365.eu en prod, sinon affiche pas le lien.
const MARKETING_URL = import.meta.env.VITE_MARKETING_URL ?? 'https://nis365.eu';

async function fetchLatestNews(): Promise<NewsItem[]> {
  try {
    const res = await fetch(`${MARKETING_URL}/api/news?limit=5`, { credentials: 'omit' });
    if (!res.ok) return [];
    const json = await res.json();
    const items = json.data?.items ?? json.items ?? [];
    return items.map((n: { id: string; slug: string; title: string; summary: string; category?: NewsCategory; publishedAt: string }) => ({
      id:       n.id,
      slug:     n.slug,
      date:     n.publishedAt,
      title:    n.title,
      summary:  n.summary,
      category: n.category,
    }));
  } catch {
    return [];
  }
}

interface SidebarProps {
  selected: string;
}

export function Sidebar({ selected }: SidebarProps) {
  const { t } = useTranslation();
  const [news, setNews] = useState<NewsItem[]>([]);
  const navigate = useNavigate();
  const license = useLicense();
  const onboardingQ = useQuery({ queryKey: ['onboarding-status'], queryFn: () => onboardingApi.status(), staleTime: 60_000, retry: false });
  const partnerSlug = onboardingQ.data?.partnerSlug ?? '';
  const partnerId   = onboardingQ.data?.partnerId ?? '';
  const hasPartner  = !!(partnerSlug || partnerId);

  // Charge la fiche du partenaire rattaché pour afficher son logo dans l'encadré.
  const partnersQ = useQuery({
    queryKey: ['public-partners'],
    queryFn:  () => partnersApi.list(),
    staleTime: 60_000,
    retry:     false,
    enabled:   hasPartner,
  });
  const myPartner = (partnersQ.data?.partners ?? []).find(
    (p) => (partnerSlug && p.slug === partnerSlug) || (partnerId && p.id === partnerId)
  );

  // Compute gating
  // Normalise plan en lowercase : des cles historiques peuvent stocker
  // 'Business' (capitalize) au lieu de 'business' - on tolere les deux.
  const hasActiveLicense = Boolean(license.hasLicense && license.status?.valid);
  const currentPlan      = (license.status?.plan ?? '').toLowerCase();
  const hasProPlan       = hasActiveLicense && PRO_OR_HIGHER.has(currentPlan);
  const hasBusinessPlan  = hasActiveLicense && BUSINESS_OR_HIGHER.has(currentPlan);

  // Plan EN COURS affiché à côté de l'entrée Abonnement (plan effectif, pas le signup).
  const PLAN_LABEL: Record<string, string> = {
    starter: 'Starter', pro: 'Pro', business: 'Business', enterprise: 'Enterprise',
  };
  const currentPlanLabel = (hasActiveLicense && currentPlan)
    ? (PLAN_LABEL[currentPlan] ?? currentPlan)
    : t('billing.plan.trial', 'Essai');

  // v2026.06.151 - Anti-FOUC : pendant le chargement initial de la licence
  // (license.loading=true), on assume "tout est debloque" pour ne pas faire
  // flasher les pills AMELIORER puis disparaitre. Apres reponse du backend,
  // les pills s'affichent reellement si la licence ne couvre pas la feature.
  function isLocked(item: NavItem): boolean {
    if (item.comingSoon)               return true;
    if (license.loading)               return false;     // <-- anti-FOUC
    if (item.requires === 'license')   return !hasActiveLicense;
    if (item.requires === 'pro')       return !hasProPlan;
    if (item.requires === 'business')  return !hasBusinessPlan;
    return false;
  }

  useEffect(() => {
    fetchLatestNews().then(setNews).catch(() => setNews([]));
  }, []);

  return (
    <aside
      style={{
        width:           320,   // v2026.06.109 - elargi de 280 a 320 pour eviter overlap badge PREVIEW + tag AMELIORER
        backgroundColor: tokens.colorNeutralBackground2,
        borderRight:     `1px solid ${tokens.colorNeutralStroke2}`,
        display:         'flex',
        flexDirection:   'column',
        height:          '100vh',
        position:        'sticky',
        top:             0,
      }}
    >
      {/* Logo aligné à gauche (le tenant connecté est désormais affiché dans le tableau de bord). */}
      <div style={{
        padding: '20px 16px 16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        <div style={{ display: 'inline-block' }}>
          <BrandLogo height={74} />
        </div>
      </div>

      {/* Nav items */}
      <nav style={{
        padding:       '4px 12px',
        display:       'flex',
        flexDirection: 'column',
        gap:           2,
      }}>
        {items.map((item) => {
          const locked = isLocked(item);
          const label  = (item.value === 'partners' && hasPartner) ? t('nav.myPartner') : t(item.labelKey);

          // v2026.06 - Quand le client est rattaché à un partenaire, l'entrée
          // "Mon partenaire" devient un encadré cliquable montrant le logo.
          if (item.value === 'partners' && hasPartner) {
            const initial = (myPartner?.name ?? '').charAt(0).toUpperCase();
            return (
              <NavLink
                key={item.value}
                to={item.path}
                className={({ isActive }) => `sidebar-partner-box${isActive ? ' is-active' : ''}`}
              >
                <div className="sidebar-partner-head">
                  <HandshakeRegular style={{ width: 18, height: 18 }} />
                  <span>{label}</span>
                </div>
                <div className="sidebar-partner-body">
                  {myPartner?.logoUrl ? (
                    <img src={myPartner.logoUrl} alt={myPartner.name ?? ''} className="sidebar-partner-logo" />
                  ) : (
                    <div className="sidebar-partner-initial">{initial || '🤝'}</div>
                  )}
                  {myPartner?.name && <span className="sidebar-partner-name">{myPartner.name}</span>}
                </div>
              </NavLink>
            );
          }

          // Si lock : on cliquera quand meme (la page affichera le upsell)
          // mais on grise + tag
          return (
            <NavLink
              key={item.value}
              to={item.path}
              end={item.path === '/'}
              data-tag-color={item.tagColor === 'green' ? 'green' : undefined}
              className={({ isActive }) =>
                `sidebar-nav-item${isActive ? ' is-active' : ''}${locked ? ' is-locked' : ''}`
              }
              onClick={() => {
                if (locked) {
                  // On laisse passer la nav mais la page affichera l upsell
                  // (pas de preventDefault - on veut que le user voit pourquoi c est lock)
                }
              }}
            >
              <span className="sidebar-nav-indicator" />
              <span className="sidebar-nav-icon">
                {item.icon}
              </span>
              <span className="sidebar-nav-label">{label}</span>
              {/* Plan en cours à côté de l'Abonnement */}
              {item.value === 'billing' && (
                <span
                  style={{
                    fontSize:      9,
                    fontWeight:    700,
                    padding:       '2px 6px',
                    marginLeft:    6,
                    borderRadius:  4,
                    background:    hasActiveLicense ? 'rgba(31,122,69,0.15)' : 'rgba(217,132,43,0.18)',
                    color:         hasActiveLicense ? '#1F7A45' : '#A8631A',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    lineHeight:    1,
                    display:       'inline-block',
                  }}
                  title={`Plan en cours : ${currentPlanLabel}`}
                >
                  {currentPlanLabel}
                </span>
              )}
              {/* v2026.06.115 - drapeau BE uniquement sur Mapping CyFun (Azure retire) */}
              {item.value === 'cyfun' && (
                <FlagIcon code="be" size={14} style={{ marginLeft: 4 }} title="Belgique" />
              )}
              {/* v2026.06.108 - badge 'preview' discret a cote du label */}
              {item.preview && (
                <span
                  style={{
                    fontSize:        9,
                    fontWeight:      600,
                    padding:         '2px 5px',
                    marginLeft:      6,
                    borderRadius:    3,
                    background:      'rgba(120, 120, 140, 0.15)',
                    color:           '#5A5A7A',
                    textTransform:   'uppercase',
                    letterSpacing:   '0.04em',
                    lineHeight:      1,
                    display:         'inline-block',
                  }}
                >
                  preview
                </span>
              )}
              {locked && (
                item.comingSoon ? (
                  <span
                    className={`sidebar-nav-tag sidebar-nav-tag-soon${item.tagColor === 'green' ? ' sidebar-nav-tag-soon-green' : ''}`}
                    aria-label={t('common.comingSoon')}
                  >
                    {t('tag.comingSoon')}
                  </span>
                ) : (
                  <span
                    className="sidebar-nav-tag"
                    onClick={(e) => {
                      // Le tag est cliquable independamment du NavLink parent : redirige vers /billing
                      e.preventDefault();
                      e.stopPropagation();
                      navigate('/billing');
                    }}
                    role="button"
                    aria-label={`${t('tag.upgrade')} : ${label}`}
                  >
                    {t('tag.upgrade')} →
                  </span>
                )
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Spacer */}
      <div style={{ flex: 1, minHeight: 16 }} />

      {/* News panel desactive : redondant avec le carousel d actualites du Dashboard.
          Le fetch reste en place (fetchLatestNews dans useEffect) au cas ou on veuille
          le reactiver pour un autre widget. */}
      {/* {news.length > 0 && <NewsPanel items={news} selected={selected} />} */}

      {/* Footer version */}
      <div style={{
        padding: '10px 24px 14px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
      }}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, fontSize: 11 }}>
          v0.2.0 · catalogue 2026.05.1
        </Caption1>
      </div>

      {/* =====================================================================
          Styles globaux nav items
          - Pas de border-left : on utilise une "pill" indicator a gauche.
          - Etat actif : fond brand sature + indicator visible.
          - Hover normal : fond gris doux.
          - Hover actif : indicator un cran plus grand + fond brand cran plus
            sature (no bold, no transform brusque).
          - Transition 180ms easing pour tout.
      ===================================================================== */}
      <style>{`
        .sidebar-nav-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 12px 16px 12px 22px;
          border-radius: 10px;
          text-decoration: none;
          color: ${tokens.colorNeutralForeground2};
          background-color: transparent;
          font-size: 14.5px;
          font-weight: 500;
          line-height: 1.25;
          transition: background-color 180ms ease,
                      color           180ms ease;
        }
        .sidebar-nav-item .sidebar-nav-indicator {
          position: absolute;
          left: 8px;
          top: 50%;
          width: 3px;
          height: 0;
          border-radius: 3px;
          background-color: ${tokens.colorBrandForeground1};
          transform: translateY(-50%);
          opacity: 0;
          transition: height 180ms ease, opacity 180ms ease;
        }
        .sidebar-nav-item .sidebar-nav-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          flex-shrink: 0;
          color: ${tokens.colorNeutralForeground3};
          transition: color 180ms ease;
        }
        .sidebar-nav-item .sidebar-nav-label {
          flex: 1;
        }
        /* Tag Upgrade - cliquable, bleu brand */
        .sidebar-nav-item .sidebar-nav-tag {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 3px 9px;
          background: ${tokens.colorBrandBackground};
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          border-radius: 999px;
          flex-shrink: 0;
          cursor: pointer;
          border: 1px solid ${tokens.colorBrandStroke1};
          transition: background 150ms ease, transform 150ms ease;
          pointer-events: auto;
        }
        .sidebar-nav-item .sidebar-nav-tag:hover {
          background: ${tokens.colorBrandBackgroundHover ?? tokens.colorBrandBackground};
          transform: scale(1.05);
        }
        /* Tag "Bientot" - neutre, non cliquable */
        .sidebar-nav-item .sidebar-nav-tag-soon {
          background: ${tokens.colorNeutralBackground3};
          color: ${tokens.colorNeutralForeground3};
          border: 1px solid ${tokens.colorNeutralStroke2};
          cursor: default;
        }
        .sidebar-nav-item .sidebar-nav-tag-soon:hover {
          background: ${tokens.colorNeutralBackground3};
          transform: none;
        }
        /* Variante verte (ex: Partenaires - ecosysteme) */
        .sidebar-nav-item .sidebar-nav-tag-soon-green {
          background: #E6F4EC;
          color: #1F7A45;
          border: 1px solid #B5D9C2;
        }
        .sidebar-nav-item .sidebar-nav-tag-soon-green:hover {
          background: #E6F4EC;
        }
        /* Item Partenaires : touche verte subtile sur l icone */
        .sidebar-nav-item[data-tag-color="green"] .sidebar-nav-icon {
          color: #1F7A45 !important;
          opacity: 0.7;
        }
        /* Etat locked (license/plan manquant) - subtle gris + icone moins saturee */
        .sidebar-nav-item.is-locked {
          color: ${tokens.colorNeutralForeground3};
        }
        .sidebar-nav-item.is-locked .sidebar-nav-icon {
          color: ${tokens.colorNeutralForeground4};
        }
        .sidebar-nav-item.is-locked .sidebar-nav-label {
          opacity: 0.85;
        }
        .sidebar-nav-item.is-locked:hover {
          background-color: ${tokens.colorNeutralBackground3};
        }
        /* hover sur item inactif */
        .sidebar-nav-item:not(.is-active):hover {
          background-color: ${tokens.colorNeutralBackground3};
          color: ${tokens.colorNeutralForeground1};
          color: ${tokens.colorNeutralForeground1};
        }
        .sidebar-nav-item.is-active .sidebar-nav-icon {
          color: ${tokens.colorBrandForeground1};
        }
        .sidebar-nav-item.is-active .sidebar-nav-indicator {
          opacity: 1;
          height: 22px;
        }
        .sidebar-nav-item.is-active:hover {
          background-color: ${tokens.colorBrandBackground2Hover ?? tokens.colorBrandBackground2};
        }
        .sidebar-nav-item:focus-visible {
          outline: 2px solid ${tokens.colorBrandStroke1};
          outline-offset: 2px;
        }
        /* v2026.06 - Encadré "Mon partenaire" (logo + zone cliquable élargie) */
        .sidebar-partner-box {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin: 6px 0;
          padding: 12px 14px;
          border-radius: 12px;
          text-decoration: none;
          border: 1px solid ${tokens.colorNeutralStroke2};
          background: ${tokens.colorNeutralBackground1};
          transition: border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease;
        }
        .sidebar-partner-box:hover {
          border-color: ${tokens.colorBrandStroke1};
          background: ${tokens.colorNeutralBackground1Hover ?? tokens.colorNeutralBackground1};
          box-shadow: ${tokens.shadow4};
        }
        .sidebar-partner-box.is-active {
          border-color: ${tokens.colorBrandStroke1};
          background: ${tokens.colorBrandBackground2};
        }
        .sidebar-partner-box:focus-visible {
          outline: 2px solid ${tokens.colorBrandStroke1};
          outline-offset: 2px;
        }
        .sidebar-partner-head {
          display: flex;
          align-items: center;
          gap: 10px;
          color: ${tokens.colorNeutralForeground2};
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .sidebar-partner-head svg { color: #1F7A45; }
        .sidebar-partner-body {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 6px 2px 2px;
        }
        .sidebar-partner-logo {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          object-fit: contain;
          background: #fff;
          padding: 4px;
          border: 1px solid ${tokens.colorNeutralStroke2};
          flex-shrink: 0;
        }
        .sidebar-partner-initial {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          color: ${tokens.colorNeutralForeground2};
          background: ${tokens.colorNeutralBackground3};
          border: 1px solid ${tokens.colorNeutralStroke2};
        }
        .sidebar-partner-name {
          font-size: 14px;
          font-weight: 600;
          color: ${tokens.colorNeutralForeground1};
          line-height: 1.3;
        }
      `}</style>
    </aside>
  );
}
